import React from 'react'
import styled from 'styled-components'
import Plot from '~/prix/react/components/plot'
import TimeSeriesPreview from '~/components/timeSeriesPreview'
import { stripParens, formatBR, formatPercent } from '../utils/format'
import { axisBase, buildDayOfMonthTicks, buildDayOfYearTicks } from '../utils/axis'
import {
  parseISO,
  getYear,
  getMonth,
  startOfYear,
  startOfMonth,
  addDays,
  isLeapYear,
  format as formatDateFns,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  TimeSeriesComparisonProps,
  TimeSeriesComparisonPayload,
  SeriesInput,
  SeriesNormalized,
} from '../visualization.types'

import {
  computeChange,
  getComparisonDisplayValues,
  getComparisonSeriesDisplayValue,
  getComparisonValueLabel,
} from '../utils/metrics'
import { Wrapper, MetricsRow, MetricValueWrap } from '../styles'
import { readMeta, resolveSeriesYear } from '../utils/extract'
import { barPreviewTitles } from '../utils/titles'

const BarsPreview = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px 10px var(--bar-preview-padding-left, 16px);
  margin-left: var(--bar-preview-offset, -12px);
`

const BarRow = styled.div`
  display: grid;
  grid-template-columns: var(--bar-label-width, 44px) minmax(0, 1fr) var(--bar-value-width, 8.5ch);
  align-items: center;
  width: 100%;
  column-gap: 8px;
`

const BarLabel = styled.span<{ $isShortMonthLabel?: boolean }>`
  width: var(--bar-label-width, 44px);
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-align: right;
  white-space: nowrap;
  box-sizing: border-box;
  padding-right: 0px;
`

const BarTrack = styled.div`
  position: relative;
  flex: 1;
  height: 12px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`

const BarFill = styled.div<{ $width: number; $color: string }>`
  width: ${({ $width }) => `${$width}%`};
  height: 100%;
  background: ${({ $color }) => $color};
  border-radius: inherit;
`

const BarValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-align: left;
  white-space: nowrap;
  width: var(--bar-value-width, 8.5ch);
`

type MetricItem = { label: 'MoM' | 'YoY' | 'YTD'; value: number | null }

export default function TimeSeriesComparison({
  visualization,
  isExpanded,
  activeButton,
  activeLabel,
  insightTitle,
}: TimeSeriesComparisonProps) {
  const payload = visualization?.payload as TimeSeriesComparisonPayload
  const rawSeries: SeriesInput[] = Array.isArray(payload.series) ? payload.series : []

  const chartHeight = !isExpanded ? 60 : 205

  const series: SeriesNormalized[] = rawSeries.map((s, i) => ({
    name: stripParens(s.name ?? s.label) ?? `Série ${i + 1}`,
    color: s.color ?? '#2563eb',
    fillColor: s.fillColor,
    values: (s.y ?? []) as number[],
    year: s.year as number | undefined,
    labelRaw: (s.label ?? s.name ?? '') as string,
  }))

  // métrica compacta
  const change = computeChange(payload, series)
  const metricValues: MetricItem[] = change
    ? [
        {
          label: change.kind as MetricItem['label'],
          value: isFinite(change.percentChange) ? change.percentChange / 100 : null,
        },
      ]
    : []

  const refISO: string =
    payload?.meta?.reference?.start ||
    payload?.reference?.start ||
    new Date().toISOString().slice(0, 10)

  const comparisonISO: string | undefined =
    payload?.meta?.comparison?.start || payload?.comparison?.start

  const refDate = parseISO(refISO)
  const comparisonDate = comparisonISO ? parseISO(comparisonISO) : undefined
  const fallbackRefYear = getYear(refDate)
  const meta = readMeta(payload)

  if (!isExpanded) {
    const previewTitle = insightTitle ?? payload?.title ?? payload?.meta?.labels?.title
    const shouldRenderBars = previewTitle ? barPreviewTitles.has(previewTitle.trim()) : false
    const normalizedPreviewTitle = (previewTitle ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
    const shouldUseMonthYearLabelOnLeft = new Set([
      'variacao mensal (ano contra ano)',
      'tendencia do mes atual (comparacao sequencial)',
      'tendencia do mes atual (comparacao anual)',
      'variacao mes a mes',
    ]).has(normalizedPreviewTitle)
    const subtitleNormalized = payload?.subtitle?.toLowerCase() ?? ''
    const hasInsufficientVariationData =
      (payload?.meta as { percentageChange?: number | null } | undefined)?.percentageChange ===
        null ||
      subtitleNormalized.includes('não há dados suficientes para calcular a variação') ||
      subtitleNormalized.includes('não ha dados suficientes para calcular a variacao')
    const isZero = (value: number) => Math.abs(value) < 1e-9
    const sumValues = (values: number[]) => values.reduce((acc, v) => acc + (v ?? 0), 0)
    const comparisonValueLabel = getComparisonValueLabel(payload?.meta?.aggregation)
    const formatBarLabel = (label: string | undefined, year: number) => {
      if (!label) return String(year)
      return label.includes(String(year)) ? label : `${label} ${year}`
    }
    const toMonthYearShortLabel = (index: number, year: number, label?: string) => {
      const baseDate = index === 0 ? comparisonDate : refDate
      if (baseDate && !Number.isNaN(baseDate.getTime())) {
        const monthShort = formatDateFns(baseDate, 'MMM', { locale: ptBR }).replace('.', '').trim()
        if (monthShort) {
          const month = monthShort.charAt(0).toUpperCase() + monthShort.slice(1).toLowerCase()
          return `${month}/${baseDate.getFullYear()}`
        }
      }

      if (!label) return undefined
      const firstToken = label
        .replace(/\d{4}/g, '')
        .replace(/\bde\b/gi, '')
        .trim()
        .split(/\s+/)[0]
      if (!firstToken) return undefined
      const month =
        firstToken.slice(0, 3).charAt(0).toUpperCase() + firstToken.slice(1, 3).toLowerCase()
      return `${month}/${year}`
    }
    const buildPreviewRows = () =>
      [0, 1]
        .map(index => {
          const current = series[index]
          const values = current?.values ?? []
          const hasValues = values.length > 0
          if (!hasValues) return null

          const year = resolveSeriesYear(current, index, meta, fallbackRefYear)
          const label = stripParens(current?.labelRaw || current?.name)
          const value = getComparisonSeriesDisplayValue(payload, index, sumValues(values))
          const valueText = formatBR(value)
          const title = `${formatBarLabel(label ?? '', year)}\n${comparisonValueLabel}: ${valueText}`
          const leftLabel = shouldUseMonthYearLabelOnLeft
            ? (toMonthYearShortLabel(index, year, label ?? undefined) ?? String(year))
            : String(year)

          return {
            key: `${year}-${index}-${leftLabel}`,
            year,
            leftLabel,
            value,
            valueText,
            color: current?.color ?? (index === 0 ? '#2563eb' : '#9ca3af'),
            title,
          }
        })
        .filter((row): row is NonNullable<typeof row> => Boolean(row))
    const renderPreviewRows = (rows: ReturnType<typeof buildPreviewRows>) => {
      const maxTotal = Math.max(...rows.map(row => Math.abs(row.value)), 0)
      const toPercent = (value: number) =>
        maxTotal > 0 ? Math.max(0, Math.min(100, (Math.abs(value) / maxTotal) * 100)) : 0
      const countDigits = (value: string) => value.replace(/\D/g, '').length
      const maxDigits = Math.max(...rows.map(row => countDigits(row.valueText)), 1)
      const valueWidth = maxDigits <= 7 ? '5.5ch' : maxDigits <= 9 ? '7ch' : '10ch'
      const labelWidth = shouldUseMonthYearLabelOnLeft ? '64px' : '44px'

      return (
        <Wrapper $height={chartHeight}>
          <BarsPreview
            style={{
              ['--bar-value-width' as string]: valueWidth,
              ['--bar-label-width' as string]: labelWidth,
              ['--bar-preview-padding-left' as string]: shouldUseMonthYearLabelOnLeft
                ? '19px'
                : '16px',
              ['--bar-preview-offset' as string]: shouldUseMonthYearLabelOnLeft ? '-10px' : '-12px',
            }}
          >
            {rows.map(row => (
              <BarRow key={row.key}>
                <BarLabel $isShortMonthLabel={shouldUseMonthYearLabelOnLeft}>
                  {row.leftLabel}
                </BarLabel>
                <BarTrack title={row.title}>
                  <BarFill $width={toPercent(row.value)} $color={row.color} />
                </BarTrack>
                <BarValue>{row.valueText}</BarValue>
              </BarRow>
            ))}
          </BarsPreview>
        </Wrapper>
      )
    }

    if (shouldRenderBars && change) {
      const minDays = Math.min(series[0]?.values.length ?? 0, series[1]?.values.length ?? 0)
      const { comparisonValue, referenceValue, valueLabel } = getComparisonDisplayValues(
        payload,
        change,
      )
      const shouldHideBarsOnPreview =
        hasInsufficientVariationData && isZero(comparisonValue) && isZero(referenceValue)

      if (shouldHideBarsOnPreview) {
        const rows = buildPreviewRows()
        if (rows.some(row => !isZero(row.value))) {
          return renderPreviewRows(rows)
        }

        const longestPreview = Math.max(...series.map(s => s.values?.length ?? 0)) || 1
        const isAnnualPreview = longestPreview >= 330
        return (
          <Wrapper $height={chartHeight}>
            <TimeSeriesPreview
              series={series}
              disableYAxis
              disablePoints
              padding={isAnnualPreview ? 8 : 0}
            />
          </Wrapper>
        )
      }

      const maxTotal = Math.max(Math.abs(comparisonValue), Math.abs(referenceValue), 0)
      const toPercent = (value: number) =>
        maxTotal > 0 ? Math.max(0, Math.min(100, (Math.abs(value) / maxTotal) * 100)) : 0
      const comparisonYear = resolveSeriesYear(series[0], 0, meta, fallbackRefYear)
      const referenceYear = resolveSeriesYear(series[1], 1, meta, fallbackRefYear)
      const comparisonLabel = stripParens(series[0]?.labelRaw || series[0]?.name)
      const referenceLabel = stripParens(series[1]?.labelRaw || series[1]?.name)
      const comparisonLeftLabel = shouldUseMonthYearLabelOnLeft
        ? (toMonthYearShortLabel(0, comparisonYear, comparisonLabel ?? undefined) ??
          String(comparisonYear))
        : String(comparisonYear)
      const referenceLeftLabel = shouldUseMonthYearLabelOnLeft
        ? (toMonthYearShortLabel(1, referenceYear, referenceLabel ?? undefined) ??
          String(referenceYear))
        : String(referenceYear)
      const comparisonValueText = formatBR(comparisonValue)
      const referenceValueText = formatBR(referenceValue)
      const countDigits = (value: string) => value.replace(/\D/g, '').length
      const maxDigits = Math.max(countDigits(comparisonValueText), countDigits(referenceValueText))
      const valueWidth = maxDigits <= 7 ? '5.5ch' : maxDigits <= 9 ? '7ch' : '10ch'
      const labelWidth = shouldUseMonthYearLabelOnLeft ? '64px' : '44px'
      const maxSeriesDays = Math.max(series[0]?.values.length ?? 0, series[1]?.values.length ?? 0)
      const daysInfo = minDays > 0 && minDays < maxSeriesDays ? `${minDays} dias` : undefined
      const comparisonTitle = `${formatBarLabel(comparisonLabel ?? '', comparisonYear)}${
        daysInfo ? ` (${daysInfo})` : ''
      }\n${valueLabel}: ${comparisonValueText}`
      const referenceTitle = `${formatBarLabel(referenceLabel ?? '', referenceYear)}${
        daysInfo ? ` (${daysInfo})` : ''
      }\n${valueLabel}: ${referenceValueText}`

      return (
        <Wrapper $height={chartHeight}>
          <BarsPreview
            style={{
              ['--bar-value-width' as string]: valueWidth,
              ['--bar-label-width' as string]: labelWidth,
              ['--bar-preview-padding-left' as string]: shouldUseMonthYearLabelOnLeft
                ? '19px'
                : '16px',
              ['--bar-preview-offset' as string]: shouldUseMonthYearLabelOnLeft ? '-10px' : '-12px',
            }}
          >
            <BarRow>
              <BarLabel $isShortMonthLabel={shouldUseMonthYearLabelOnLeft}>
                {comparisonLeftLabel}
              </BarLabel>
              <BarTrack title={comparisonTitle}>
                <BarFill
                  $width={toPercent(comparisonValue)}
                  $color={series[0]?.color ?? '#2563eb'}
                />
              </BarTrack>
              <BarValue>{comparisonValueText}</BarValue>
            </BarRow>
            <BarRow>
              <BarLabel $isShortMonthLabel={shouldUseMonthYearLabelOnLeft}>
                {referenceLeftLabel}
              </BarLabel>
              <BarTrack title={referenceTitle}>
                <BarFill
                  $width={toPercent(referenceValue)}
                  $color={series[1]?.color ?? '#9ca3af'}
                />
              </BarTrack>
              <BarValue>{referenceValueText}</BarValue>
            </BarRow>
          </BarsPreview>
        </Wrapper>
      )
    }

    if (shouldRenderBars && !change) {
      const rows = buildPreviewRows()
      if (rows.some(row => !isZero(row.value))) return renderPreviewRows(rows)
    }

    const longestPreview = Math.max(...series.map(s => s.values?.length ?? 0)) || 1
    const isAnnualPreview = longestPreview >= 330
    return (
      <Wrapper $height={chartHeight}>
        <TimeSeriesPreview
          series={series}
          disableYAxis
          disablePoints
          padding={isAnnualPreview ? 8 : 0}
        />
      </Wrapper>
    )
  }

  // base para séries não anuais
  const refMonth0 = getMonth(refDate)
  const comparisonMonth0 = comparisonDate ? getMonth(comparisonDate) : refMonth0

  // eixo X
  const longest = Math.max(...series.map(s => s.values?.length ?? 0)) || 1
  const x = Array.from({ length: longest }, (_, i) => i + 1)
  const isAnnual = longest >= 330

  // ticks X
  let xTickVals: number[] = []
  let xTickTexts: string[] = []
  let tickangle: number | undefined = 0
  let xMaxForRange = longest

  if (isAnnual) {
    const baseYear = meta.comparisonYear ?? meta.refYear ?? fallbackRefYear
    const totalDaysThisYear = isLeapYear(new Date(Date.UTC(baseYear, 0, 1))) ? 366 : 365
    const { vals, texts } = buildDayOfYearTicks(totalDaysThisYear)
    xTickVals = vals
    xTickTexts = texts
    tickangle = 0
    xMaxForRange = Math.max(longest, totalDaysThisYear)
  } else {
    const t = buildDayOfMonthTicks(longest)
    xTickVals = t.vals
    xTickTexts = t.texts
    tickangle = 0
  }

  const basePad = Math.max(0.5, Math.round(xMaxForRange * 0.02))
  const xPadDays = isAnnual ? Math.max(6, basePad) : basePad
  const xRange: [number, number] = [0.5 - xPadDays, Math.max(1, xMaxForRange) + 0.5 + xPadDays]

  const defaultActiveFromMeta: string | undefined = isAnnual
    ? undefined
    : payload?.meta?.labels?.reference

  const resolvedActiveGlobal: string | undefined = isAnnual
    ? undefined
    : (activeLabel ?? activeButton ?? defaultActiveFromMeta)

  const resolvedActiveNormalized: string | undefined = resolvedActiveGlobal
    ? (stripParens(String(resolvedActiveGlobal)) ?? undefined)
    : undefined

  const data: Partial<Plotly.PlotData>[] = series.map((s, si) => {
    const year = resolveSeriesYear(s, si, meta, fallbackRefYear)

    // Use local dates to avoid timezone shifting tooltips to the previous day/month.
    const baseMonth0 = si === 0 ? comparisonMonth0 : refMonth0
    const baseDate = isAnnual
      ? startOfYear(new Date(year, 0, 1))
      : startOfMonth(new Date(year, baseMonth0, 1))

    const xs = x.slice(0, s.values.length)

    const hovertext = s.values.map((yv: number, i: number) => {
      const day = addDays(baseDate, xs[i] - 1)
      const label = formatDateFns(day, 'dd/MM/yyyy', { locale: ptBR })
      return `Data: ${label}<br>Valor: ${formatBR(yv)}`
    })

    const isActiveSeries =
      Boolean(resolvedActiveNormalized) &&
      (() => {
        const target = resolvedActiveNormalized as string
        const labelNorm = stripParens(String(s.labelRaw)) ?? ''
        return (
          s.name === target ||
          labelNorm === target ||
          s.name?.includes(target) ||
          labelNorm.includes(target)
        )
      })()

    return {
      x: xs,
      y: s.values,
      type: 'scatter',
      mode: 'lines+markers',
      line: { color: s.color, width: isAnnual ? 3 : isActiveSeries ? 4 : 3 },
      marker: { size: isAnnual ? 5 : isActiveSeries ? 8 : 5 },
      name: s.name,
      cliponaxis: true,
      hovertext,
      hovertemplate: '%{hovertext}<extra></extra>',
    }
  })

  const layout: Partial<Plotly.Layout> = {
    height: chartHeight,
    margin: { l: 35, r: 24, t: 25, b: 10 },
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    showlegend: false,
    xaxis: {
      ...axisBase,
      showgrid: false,
      autorange: false,
      range: xRange,
      tickmode: 'array',
      tickvals: xTickVals,
      ticktext: xTickTexts,
      tickangle,
      title: '',
    },
    yaxis: { ...axisBase, title: '', nticks: 4, tickformat: '~s' },
    hovermode: 'closest',
    hoverlabel: {
      align: 'left',
      bgcolor: 'white',
      bordercolor: '#e5e7eb',
      font: { size: 13, color: '#111827', family: 'Figtree, sans-serif' },
    },
  }

  return (
    <div>
      {isExpanded && metricValues[0]?.value != null && (
        <MetricsRow>
          {metricValues.map(metric => {
            const variant: 'positive' | 'negative' | 'flat' =
              metric.value == null
                ? 'flat'
                : metric.value > 0
                  ? 'positive'
                  : metric.value < 0
                    ? 'negative'
                    : 'flat'
            return (
              <MetricValueWrap key={metric.label} $variant={variant}>
                {metric.label} {formatPercent(metric.value)}
              </MetricValueWrap>
            )
          })}
        </MetricsRow>
      )}

      <Wrapper $height={chartHeight}>
        <Plot data={data as Plotly.Data[]} layout={layout as Plotly.Layout} />
      </Wrapper>
    </div>
  )
}
